// Hooks
import type { SWRInfiniteConfiguration } from 'swr/infinite';
import useSWRInfinite from 'swr/infinite';

import type { ErrorCustom } from '@/utils/client';
// Utils
import { get } from '@/utils/client';

// Types
import type { Posts } from '@/components/pages/giveaway-picker/Types/types';

// Routes
import { GIVEAWAY_PICKER_POSTS } from '@/routes/apiToolsEndpoints';

type InstagramPostResponse = {
    data: {
        medias: Posts[];
    };
    cursor: string;
    media_count: number;
};

const getKey = (pageIndex: number, previousPageData: InstagramPostResponse, userId: string) => {
    if (!userId || (previousPageData && !previousPageData.cursor)) {
        return null;
    }

    if (pageIndex === 0) {
        return `${GIVEAWAY_PICKER_POSTS}?pageId=${userId}&limit=9`;
    }

    return `${GIVEAWAY_PICKER_POSTS}?pageId=${userId}&limit=9&cursor=${previousPageData.cursor}`;
};

const usePosts = (
    userId: string,
    options?: SWRInfiniteConfiguration<InstagramPostResponse, ErrorCustom>
) => {
    const { data, size, setSize, error, isValidating } = useSWRInfinite<
        InstagramPostResponse,
        ErrorCustom
    >(
        (pageIndex, previousPageData) => getKey(pageIndex, previousPageData, userId),
        (url) =>
            get(url).then((res) => {
                return res.data;
            }),
        {
            revalidateFirstPage: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            shouldRetryOnError: false,
            ...options
        }
    );

    const isLoadingInitialData = !data && !error && isValidating;
    const isEmpty = !data?.length;
    const isLoadingMore = size > 0 && data && typeof data[size - 1] === 'undefined' && isValidating;
    const isLoadMore = data && typeof data[data.length - 1].cursor === 'string';
    return {
        data: data ? data.flatMap((page) => page.data.medias) : [],
        loadMore: () => setSize(size + 1),
        isEmpty,
        error,
        isLoadingInitialData,
        isLoadingMore,
        isLoadMore,
        currentPage: size
    };
};

export default usePosts;
